import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAuthState } from '../app/store/authSlice.js'
import { createStatementStreamService } from '../services/statementStreamService.js'
import useSessionStorageState from './useSessionStorageState.js'

const DEFAULT_MAX_EVENTS = 250
const DEFAULT_STALE_THRESHOLD_MS = 5_000
const MAX_VALIDATION_ENTRIES = 50

const emptyArray = Object.freeze([])

const getStorageKeys = (prefix = '') => ({
  instance: `${prefix}webui-active-instance`,
  statement: `${prefix}webui-active-statement`,
})

const toEventView = (payload, thresholdMs) => {
  if (!payload?.data) {
    return null
  }

  const { data } = payload
  const receivedAt = payload.receivedAt ?? Date.now()
  const sourceTimestamp = data.timestamp ? Date.parse(data.timestamp) : null
  const lagMs = sourceTimestamp ? Math.max(0, receivedAt - sourceTimestamp) : null

  return {
    ...data,
    id: payload.id ?? `${data.instanceName ?? 'event'}-${receivedAt}`,
    receivedAt,
    lagMs,
    isStale: lagMs !== null ? lagMs > thresholdMs : false,
    raw: payload.raw,
  }
}

const sanitizeString = (value) => {
  if (!value) return ''
  return String(value).trim()
}

const useStatementStream = ({
  maxEvents = DEFAULT_MAX_EVENTS,
  staleThresholdMs = DEFAULT_STALE_THRESHOLD_MS,
  autoStart = true,
  storagePrefix = '',
} = {}) => {
  const serviceRef = useRef(null)
  const stopRef = useRef(null)
  const headersRef = useRef({})

  const { apiKey, writeKey, isAuthenticated } = useSelector(selectAuthState)

  const [events, setEvents] = useState([])
  const [connectionState, setConnectionState] = useState('idle')
  const [error, setError] = useState(null)
  const [lastEventAt, setLastEventAt] = useState(null)
  const [lagMs, setLagMs] = useState(0)
  const [isStale, setIsStale] = useState(false)
  const [nextRetry, setNextRetry] = useState(null)
  const [connectedAt, setConnectedAt] = useState(null)
  const [catalog, setCatalog] = useState({
    instances: [],
    statementsByInstance: {},
  })
  const [validationLog, setValidationLog] = useState([])

  const STORAGE_KEYS = useMemo(() => getStorageKeys(storagePrefix), [storagePrefix])

  const [activeInstance, setActiveInstance] = useSessionStorageState(STORAGE_KEYS.instance, '')
  const [activeStatement, setActiveStatement] = useSessionStorageState(STORAGE_KEYS.statement, '')

  const [shouldStream, setShouldStream] = useState(autoStart && isAuthenticated)

  if (!serviceRef.current) {
    serviceRef.current = createStatementStreamService({
      getAuthHeaders: () => headersRef.current,
    })
  }

  useEffect(() => {
    headersRef.current = (() => {
      const headers = {}
      if (apiKey) {
        headers['x-api-key'] = apiKey
      }
      if (writeKey) {
        headers.writekey = writeKey
      }
      return headers
    })()
  }, [apiKey, writeKey])

  useEffect(() => {
    if (!isAuthenticated) {
      setShouldStream(false)
    } else if (autoStart) {
      setShouldStream(true)
    }
  }, [isAuthenticated, autoStart])

  useEffect(() => {
    if (!shouldStream) {
      return
    }

    if (!isAuthenticated || !apiKey) {
      setShouldStream(false)
    }
  }, [shouldStream, isAuthenticated, apiKey])

  useEffect(() => {
    const now = Date.now()
    if (connectionState === 'connected' && !lastEventAt) {
      setLagMs(now - (connectedAt ?? now))
      setIsStale(false)
    }
  }, [connectionState, lastEventAt, connectedAt])

  const handleEvent = useCallback(
    (payload) => {
      const eventView = toEventView(payload, staleThresholdMs)
      if (!eventView) {
        return
      }

      setEvents((previous) => {
        const next = [eventView, ...previous]
        if (next.length > maxEvents) {
          next.length = maxEvents
        }
        return next
      })

      if (eventView.instanceName) {
        setCatalog((previous) => {
          let hasChanged = false
          const instancesSet = new Set(previous.instances)
          if (!instancesSet.has(eventView.instanceName)) {
            instancesSet.add(eventView.instanceName)
            hasChanged = true
          }
          const statementsForInstance = new Set(
            previous.statementsByInstance[eventView.instanceName] ?? [],
          )
          if (eventView.statementName && !statementsForInstance.has(eventView.statementName)) {
            statementsForInstance.add(eventView.statementName)
            hasChanged = true
          }
          if (!hasChanged) {
            return previous
          }
          const sortedInstances = Array.from(instancesSet).sort()
          const statementsByInstance = {
            ...previous.statementsByInstance,
            [eventView.instanceName]: Array.from(statementsForInstance).sort(),
          }
          return {
            instances: sortedInstances,
            statementsByInstance,
          }
        })
      }

      setLastEventAt(eventView.receivedAt)
    },
    [maxEvents, staleThresholdMs],
  )

  const handleError = useCallback((streamError) => {
    setError(streamError)
  }, [])

  const handleStatusChange = useCallback((status, meta) => {
    setConnectionState(status)
    if (status === 'connected') {
      setConnectedAt(Date.now())
      setNextRetry(null)
      setError(null)
    }
    if (status === 'stopped') {
      setConnectedAt(null)
      setNextRetry(null)
    }
    if (status === 'error' && meta) {
      setError(meta)
    }
  }, [])

  const handleReconnectScheduled = useCallback((details) => {
    setNextRetry(details)
  }, [])

  const recordValidationResult = useCallback((result) => {
    if (!result) {
      return
    }

    setValidationLog((previous) => {
      const next = [
        {
          id: `${result.statementName ?? 'validation'}-${Date.now()}`,
          ...result,
          timestamp: result.timestamp ?? new Date().toISOString()
        },
        ...previous
      ]
      if (next.length > MAX_VALIDATION_ENTRIES) {
        next.length = MAX_VALIDATION_ENTRIES
      }
      return next
    })
  }, [])

  useEffect(() => {
    const service = serviceRef.current

    if (shouldStream && isAuthenticated && apiKey) {
      // Clear old data when reconnecting (e.g., API key changed)
      setEvents(emptyArray)
      setLastEventAt(null)
      setLagMs(0)
      setIsStale(false)
      setError(null)
      setValidationLog([])

      const stop = service.start({
        instanceName: sanitizeString(activeInstance) || undefined,
        statementName: sanitizeString(activeStatement) || undefined,
        onEvent: handleEvent,
        onError: handleError,
        onStatusChange: handleStatusChange,
        onReconnectScheduled: handleReconnectScheduled,
      })
      stopRef.current = stop
      return () => {
        stop()
        stopRef.current = null
      }
    }

    service.stop()
    stopRef.current = null
    setConnectionState('idle')
    setNextRetry(null)
    return undefined
  }, [
    shouldStream,
    isAuthenticated,
    apiKey,
    activeInstance,
    activeStatement,
    handleEvent,
    handleError,
    handleStatusChange,
    handleReconnectScheduled,
  ])

  useEffect(() => {
    if (!shouldStream) {
      serviceRef.current?.stop()
    }
  }, [shouldStream])

  const selectionRef = useRef({ instance: sanitizeString(activeInstance), statement: sanitizeString(activeStatement) })

  useEffect(() => {
    const normalizedInstance = sanitizeString(activeInstance)
    const normalizedStatement = sanitizeString(activeStatement)
    const previous = selectionRef.current
    if (
      previous.instance !== normalizedInstance ||
      previous.statement !== normalizedStatement
    ) {
      setEvents(emptyArray)
      setLastEventAt(null)
      setLagMs(0)
      setIsStale(false)
      selectionRef.current = {
        instance: normalizedInstance,
        statement: normalizedStatement,
      }
    }
  }, [activeInstance, activeStatement])

  useEffect(() => {
    if (!shouldStream) {
      return undefined
    }

    const updateLag = () => {
      const now = Date.now()
      if (lastEventAt) {
        const diff = now - lastEventAt
        setLagMs(diff)
        setIsStale(diff >= staleThresholdMs)
      } else if (connectedAt) {
        const diff = now - connectedAt
        setLagMs(diff)
        setIsStale(diff >= staleThresholdMs)
      } else {
        setLagMs(0)
        setIsStale(false)
      }
    }

    updateLag()
    const interval = setInterval(updateLag, 1_000)
    return () => clearInterval(interval)
  }, [lastEventAt, staleThresholdMs, connectedAt, shouldStream])

  useEffect(() => {
    return () => {
      stopRef.current?.()
      serviceRef.current?.stop()
    }
  }, [])

  const connect = useCallback(() => {
    setShouldStream(true)
    setError(null)
  }, [])

  // Disconnect is no longer exposed - streams stay connected when authenticated
  // To go offline, user must select "Offline" in the API key selector

  const retry = useCallback(() => {
    setError(null)
    setNextRetry(null)
    serviceRef.current?.manualReconnect()
  }, [])

  const clearEvents = useCallback(() => {
    setEvents(emptyArray)
    setLastEventAt(null)
    setLagMs(0)
    setIsStale(false)
  }, [])

  const setInstance = useCallback(
    (instanceName) => {
      const normalizedInstance = sanitizeString(instanceName)
      setActiveInstance(normalizedInstance)
      if (normalizedInstance !== sanitizeString(activeInstance)) {
        setActiveStatement('')
      }
    },
    [activeInstance, setActiveInstance, setActiveStatement],
  )

  const setStatement = useCallback(
    (statementName) => {
      setActiveStatement(sanitizeString(statementName))
    },
    [setActiveStatement],
  )

  const metrics = useMemo(
    () => ({
      totalEvents: events.length,
      connectedAt,
      lastEventAt,
      lagMs,
      isStale,
      nextRetry,
    }),
    [events.length, connectedAt, lastEventAt, lagMs, isStale, nextRetry],
  )

  const eventSamples = useMemo(() => {
    return events.slice(0, 50).map((entry) => entry.raw ?? entry)
  }, [events])

  return {
    events,
    eventSamples,
    connectionState,
    error,
    lagMs,
    isStale,
    nextRetry,
    metrics,
    validationLog,
    activeInstance: sanitizeString(activeInstance),
    activeStatement: sanitizeString(activeStatement),
    instances: catalog.instances,
    statementsByInstance: catalog.statementsByInstance,
    connect,
    retry,
    clearEvents,
    setInstance,
    setStatement,
    recordValidationResult,
    isStreaming: shouldStream,
    isAuthenticated,
  }
}

export default useStatementStream
